import React, { useRef, useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usePostsStore } from '../stores/postsStore';
import { useThemeStore } from '../stores/themeStore';
import EmojiPicker from 'emoji-picker-react';

/**
 * Rich text editor with @mention autocomplete and emoji support
 * Supports: Bold, Italic, Underline, Ordered List, Unordered List, @mentions, Emojis, Hero mode
 */
const RichTextEditorWithMentions = ({
  value,
  onChange,
  postId,
  placeholder = 'Enter text...',
  className = '',
  isHero = false,
  onHeroChange
}) => {
  const { accessToken } = useAuthStore();
  const { getTagSuggestions } = usePostsStore();
  const { theme } = useThemeStore();
  const editorRef = useRef(null);
  const isUpdatingRef = useRef(false);
  const emojiPickerRef = useRef(null);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [savedSelection, setSavedSelection] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const pendingLinkRange = useRef(null);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [linkPopupPosition, setLinkPopupPosition] = useState({ top: 0, left: 0 });

  // Get actual theme (resolve 'system' to 'light' or 'dark')
  const getActualTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  };

  // Save current selection/cursor position
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      setSavedSelection(selection.getRangeAt(0).cloneRange());
    }
  };

  // Restore saved selection
  const restoreSelection = () => {
    if (savedSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);
    }
  };

  // Component mounted
  useEffect(() => {
    console.log('RichTextEditorWithMentions mounted with props:', { postId, hasAccessToken: !!accessToken });
  }, []);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && !event.target.closest('.rich-text-editor')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Initialize content
  useEffect(() => {
    if (editorRef.current && !isUpdatingRef.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current && onChange) {
      isUpdatingRef.current = true;
      const content = editorRef.current.innerHTML;
      onChange(content);

      // Check for @mention trigger
      const text = editorRef.current.innerText;
      const cursorPos = getCaretPosition();
      const textBeforeCursor = text.substring(0, cursorPos);

      console.log('RichTextEditor - handleInput:', { text, cursorPos, textBeforeCursor });

      // Find last @ symbol before cursor
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');
      console.log('RichTextEditor - lastAtIndex:', lastAtIndex);

      if (lastAtIndex !== -1) {
        const searchText = textBeforeCursor.substring(lastAtIndex + 1);
        console.log('RichTextEditor - searchText:', searchText);

        // Check if it's a valid mention search (no spaces after @)
        if (!searchText.includes(' ')) {
          console.log('RichTextEditor - Showing suggestions for:', searchText);
          setMentionSearch(searchText);
          fetchSuggestions(searchText);

          // Calculate position for suggestions dropdown
          const range = window.getSelection().getRangeAt(0);
          const rect = range.getBoundingClientRect();
          const editorRect = editorRef.current.getBoundingClientRect();

          setMentionPosition({
            top: rect.bottom - editorRect.top,
            left: rect.left - editorRect.left
          });

          setShowSuggestions(true);
        } else {
          console.log('RichTextEditor - Hiding suggestions (space found)');
          setShowSuggestions(false);
        }
      } else {
        console.log('RichTextEditor - Hiding suggestions (no @ found)');
        setShowSuggestions(false);
      }

      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  };

  const getCaretPosition = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return 0;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    return preCaretRange.toString().length;
  };

  const fetchSuggestions = async (search) => {
    console.log('RichTextEditor - fetchSuggestions called:', { search, accessToken: !!accessToken, postId });

    if (!accessToken) {
      console.log('RichTextEditor - No accessToken, skipping fetch');
      setSuggestions([]);
      return;
    }

    const result = await getTagSuggestions(accessToken, postId, search);
    console.log('RichTextEditor - fetchSuggestions result:', result);

    if (result.success) {
      setSuggestions(result.suggestions || []);
      setSelectedIndex(0);
      console.log('RichTextEditor - Set suggestions:', result.suggestions);
    } else {
      setSuggestions([]);
      console.log('RichTextEditor - Fetch failed, cleared suggestions');
    }
  };

  const insertMention = (suggestion) => {
    if (!editorRef.current) return;

    const text = editorRef.current.innerText;
    const html = editorRef.current.innerHTML;
    const cursorPos = getCaretPosition();
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const beforeMention = text.substring(0, lastAtIndex);
      const afterCursor = text.substring(cursorPos);

      // Create a clickable link for the mention
      const mentionLink = `<a href="/profile/${suggestion.id}/page/dashboard" class="text-primary-600 dark:text-primary-400 hover:underline font-semibold">${suggestion.display_name}</a>`;

      // Insert the mention as a link with a space after it
      const newHTML = beforeMention + mentionLink + '&nbsp;' + afterCursor;
      editorRef.current.innerHTML = newHTML;

      // Set cursor after the mention and space
      // We need to place cursor after the link element
      const range = document.createRange();
      const sel = window.getSelection();

      // Find the last text node after our inserted content
      const findTextNodeAfterLink = (node) => {
        if (node.nodeType === Node.TEXT_NODE) return node;
        for (let i = 0; i < node.childNodes.length; i++) {
          const found = findTextNodeAfterLink(node.childNodes[i]);
          if (found) return found;
        }
        return null;
      };

      try {
        // Place cursor after the space following the link
        const links = editorRef.current.querySelectorAll('a');
        const lastLink = links[links.length - 1];
        if (lastLink && lastLink.nextSibling) {
          range.setStart(lastLink.nextSibling, 1); // After the &nbsp;
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      } catch (e) {
        console.error('Error setting caret position after mention:', e);
      }

      handleInput();
    }

    setShowSuggestions(false);
    setMentionSearch('');
    editorRef.current.focus();
  };

  const setCaretPosition = (pos) => {
    const range = document.createRange();
    const sel = window.getSelection();
    const textNode = editorRef.current.firstChild || editorRef.current;

    try {
      range.setStart(textNode, Math.min(pos, textNode.textContent?.length || 0));
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (e) {
      console.error('Error setting caret position:', e);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const handleEmojiClick = (emojiData) => {
    if (!editorRef.current) return;

    // Focus the editor first
    editorRef.current.focus();

    // Restore the saved selection
    let range;
    if (savedSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);
      range = savedSelection;
    } else {
      // Fallback: create a range at the end
      const selection = window.getSelection();
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Insert emoji as text node
    const emojiNode = document.createTextNode(emojiData.emoji);
    range.insertNode(emojiNode);

    // Move cursor after emoji
    range.setStartAfter(emojiNode);
    range.setEndAfter(emojiNode);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    // Save the new position for next emoji
    setSavedSelection(range.cloneRange());

    setShowEmojiPicker(false);
    handleInput();
  };

  const insertLink = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (!selectedText) {
      alert('Please select some text to create a link.');
      return;
    }

    // Store the range and show modal
    pendingLinkRange.current = range.cloneRange();
    setLinkUrl('https://');
    setShowLinkModal(true);
  };

  const handleLinkSubmit = () => {
    if (!linkUrl || linkUrl === 'https://') {
      setShowLinkModal(false);
      setLinkUrl('');
      pendingLinkRange.current = null;
      return;
    }

    const range = pendingLinkRange.current;
    if (!range) return;

    const selection = window.getSelection();
    const selectedText = range.toString();

    // Create link element
    const link = document.createElement('a');
    link.href = linkUrl;
    link.target = '_blank';
    link.textContent = selectedText;

    // Delete selected content and insert link
    range.deleteContents();
    range.insertNode(link);

    // Move cursor after the link
    const newRange = document.createRange();
    newRange.setStartAfter(link);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);

    handleInput();

    // Reset modal state
    setShowLinkModal(false);
    setLinkUrl('');
    pendingLinkRange.current = null;
  };

  const handleLinkCancel = () => {
    setShowLinkModal(false);
    setLinkUrl('');
    pendingLinkRange.current = null;
  };

  const handleMouseMove = (e) => {
    // Check if hovering over a link
    const target = e.target;
    if (target.tagName?.toLowerCase() === 'a') {
      const rect = target.getBoundingClientRect();
      const editorRect = editorRef.current.getBoundingClientRect();

      setHoveredLink(target);
      setLinkPopupPosition({
        top: rect.top - editorRect.top - 35, // Position above the link
        left: rect.left - editorRect.left
      });
    } else {
      // Check if mouse is over the popup itself
      const popup = document.querySelector('.link-hover-popup');
      if (popup && popup.contains(e.target)) {
        // Keep popup visible
        return;
      }
      setHoveredLink(null);
    }
  };

  const handleRemoveLink = () => {
    if (!hoveredLink) return;

    const parent = hoveredLink.parentNode;
    while (hoveredLink.firstChild) {
      parent.insertBefore(hoveredLink.firstChild, hoveredLink);
    }
    parent.removeChild(hoveredLink);

    setHoveredLink(null);
    handleInput();
  };

  const handleKeyDown = (e) => {
    // Handle keyboard shortcuts for formatting
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
        case 'k':
          e.preventDefault();
          insertLink();
          break;
        default:
          break;
      }
    }

    // Handle suggestion navigation
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(suggestions[selectedIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    }
  };

  return (
    <div className={`rich-text-editor relative ${className}`}>
      {/* Toolbar */}
      <div className="flex gap-1 p-2 border-b border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="px-3 py-1 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Bold (Ctrl+B)"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="px-3 py-1 text-sm italic border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Italic (Ctrl+I)"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="px-3 py-1 text-sm underline border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Underline (Ctrl+U)"
        >
          U
        </button>
        <button
          type="button"
          onClick={insertLink}
          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Link (Ctrl+K)"
        >
          🔗
        </button>
        <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
        {onHeroChange && (
          <button
            type="button"
            onClick={() => onHeroChange(!isHero)}
            className={`px-3 py-1 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${
              isHero
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-700 text-primary'
            }`}
            title="Hero Mode (Large centered text)"
          >
            H
          </button>
        )}
        {onHeroChange && <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>}
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Ordered List"
        >
          1. List
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Unordered List"
        >
          • List
        </button>
        <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
        <div className="relative">
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent losing focus
              saveSelection(); // Save cursor position
            }}
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
            title="Insert Emoji"
          >
            😀
          </button>
          {showEmojiPicker && (
            <div className="absolute z-50 top-full left-0 mt-1" ref={emojiPickerRef}>
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                theme={getActualTheme()}
                width={350}
                height={400}
                searchPlaceHolder="Search emojis..."
                previewConfig={{ showPreview: false }}
              />
            </div>
          )}
        </div>
        <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
        <span className="px-3 py-1 text-xs text-secondary flex items-center">
          Type @ to mention
        </span>
      </div>

      {/* Editor */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onMouseMove={handleMouseMove}
          className={`p-3 min-h-[150px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-b focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            isHero
              ? 'text-center text-3xl font-bold py-16 text-primary'
              : 'text-primary'
          }`}
          data-placeholder={placeholder}
          style={{
            minHeight: '150px',
          }}
        />

        {/* Link Hover Popup */}
        {hoveredLink && (
          <div
            className="link-hover-popup absolute bg-gray-900 text-white text-xs rounded shadow-lg px-3 py-2 z-10 flex items-center gap-2"
            style={{
              top: `${linkPopupPosition.top}px`,
              left: `${linkPopupPosition.left}px`,
            }}
            onMouseEnter={() => {
              // Keep popup visible when hovering over it
            }}
          >
            <a
              href={hoveredLink.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-300 hover:text-blue-200 underline max-w-xs truncate"
              onClick={(e) => e.stopPropagation()}
            >
              {hoveredLink.href}
            </a>
            <button
              onClick={handleRemoveLink}
              className="ml-2 px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
              title="Remove link"
            >
              Remove
            </button>
          </div>
        )}

        {/* Suggestions Dropdown */}
        {showSuggestions && (
          <div
            className="absolute z-50 surface border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto"
            style={{
              top: `${mentionPosition.top}px`,
              left: `${mentionPosition.left}px`,
              minWidth: '250px'
            }}
          >
            {/* Search indicator header */}
            <div className="px-4 py-2 border-b border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 sticky top-0">
              <div className="text-xs text-secondary">
                {mentionSearch ? (
                  <>
                    Searching: <span className="font-semibold text-primary">{mentionSearch}</span>
                  </>
                ) : (
                  'Type to filter...'
                )}
              </div>
            </div>

            {/* Suggestions list */}
            {suggestions.length > 0 ? (
              suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.id}
                  onClick={() => insertMention(suggestion)}
                  className={`px-4 py-2 cursor-pointer text-sm ${
                    index === selectedIndex
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-primary'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold">{suggestion.display_name}</div>
                    {suggestion.priority === 1 && (
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                        Author
                      </span>
                    )}
                    {suggestion.priority === 2 && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                        Replied
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-secondary text-center">
                No matches found
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .rich-text-editor [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .rich-text-editor [contenteditable] {
          outline: none;
        }
        .rich-text-editor [contenteditable] p {
          margin: 0.5em 0;
        }
        .rich-text-editor [contenteditable] ul,
        .rich-text-editor [contenteditable] ol {
          margin: 0.5em 0;
          padding-left: 2em;
        }
        .rich-text-editor [contenteditable] li {
          margin: 0.25em 0;
        }
        .rich-text-editor [contenteditable] a {
          color: #3b82f6;
          text-decoration: underline;
          cursor: pointer;
        }
        .rich-text-editor [contenteditable] a:hover {
          color: #2563eb;
        }
      `}</style>

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-primary mb-4">Insert Link</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                URL
              </label>
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLinkSubmit();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    handleLinkCancel();
                  }
                }}
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="https://example.com"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleLinkCancel}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-secondary hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleLinkSubmit}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditorWithMentions;
